import { Choices } from './ConsultationForms';
import { useState } from 'react';
import { Action, Select, Text, type Row, type Save } from './conveyancing-forms';
export function ConveyancingPackForm({ setup, data, busy, command }: { setup: Row; data: Row; busy: boolean; command: (body: Row) => Promise<Row> }) {
  const [template, setTemplate] = useState(''),
    selected = setup.templates.find((t: Row) => t.id === template),
    questions = selected?.questions ?? setup.defaultPackQuestions,
    docs = setup.documents.filter((d: Row) => d.selectable),
    latest = data.events.filter((e: Row) => e.event_type === 'Conveyancing suite pack prepared').at(-1)?.detail;
  return (
    <Action
      title="Prepare or revise management pack"
      busy={busy}
      documents={docs}
      onSave={(v, key, form) => {
        const official = docs.find((d: Row) => d.id === v.officialFormDocumentId);
        return command({
          action: 'Prepare pack',
          templateId: template || null,
          serviceIds: form.getAll('serviceIds'),
          sourceHash: data.source.sourceHash,
          classification: v.classification,
          officialFormDocumentId: official?.id ?? null,
          officialFormVersionId: official?.version_id ?? null,
          answers: questions.map((q: Row) => ({
            key: q.key,
            answer: form.get('answer:' + q.key),
            unknown: form.has('unknown:' + q.key),
            documentIds: form.get('doc:' + q.key) ? [form.get('doc:' + q.key)] : [],
            reviewNote: form.get('review:' + q.key)
          })),
          documentIds: form.getAll('documentIds'),
          reason: v.reason,
          idempotencyKey: key
        });
      }}
    >
      <Choices
        name="serviceIds"
        title="Services covered by this pack"
        initial={data.services.filter((s: Row) => s.kind === 'Sales pack').map((s: Row) => s.id)}
        options={data.services.filter((s: Row) => !['Not required', 'Cancelled'].includes(s.status)).map((s: Row) => ({ id: s.id, title: s.title }))}
      />
      <Select
        name="template"
        label="Pack question template"
        optional
        options={setup.templates.filter((t: Row) => t.jurisdiction === data.row.transaction_details.jurisdiction).map((t: Row) => [t.id, t.title])}
        onChange={setTemplate}
      />
      <p>
        No template selected: use the standard source-backed management-information questions. Configure official-form question sets and retain completed official forms separately.
      </p>
      <Select name="classification" label="Pack description" options={['Management information pack', 'Official form with reviewed enclosure'].map((s) => [s, s])} />
      <Select name="officialFormDocumentId" label="Completed reviewed official form, where applicable" optional options={docs.map((d: Row) => [d.id, d.title])} />
      {questions.map((q: Row) => {
        const old = latest?.answers.find((a: Row) => a.key === q.key);
        return (
          <details key={template + ':' + q.key}>
            <summary>
              {q.section} — {q.question}
              {q.required ? ' (required)' : ''}
            </summary>
            <Text name={'answer:' + q.key} label={'Answer: ' + q.question} type="textarea" value={old?.answer} />
            <label>
              <input type="checkbox" name={'unknown:' + q.key} defaultChecked={old?.unknown ?? false} />
              Information unavailable or requires solicitor review
            </label>
            <Select name={'doc:' + q.key} label={'Evidence for ' + q.section} value={old?.documentIds?.[0]} optional options={docs.map((d: Row) => [d.id, d.title])} />
            <Text name={'review:' + q.key} label={'Review basis for ' + q.section} type="textarea" value={old?.reviewNote} />
          </details>
        );
      })}
    </Action>
  );
}
export function ConveyancingServiceForm({ service, setup, busy, command }: { service: Row; setup: Row; busy: boolean; command: (body: Row) => Promise<Row> }) {
  const d = service.details.review ?? {},
    [conditions, setConditions] = useState<Row[]>(d.conditions ?? [{ label: 'Requirements and evidence reviewed', satisfied: false }]),
    [newCondition, setNewCondition] = useState(''),
    membership = service.kind === 'Share or membership transfer',
    notice = ['Notice of Transfer', 'Notice of Charge'].includes(service.kind);
  return (
    <Action
      title="Review this service"
      busy={busy}
      documents={setup.documents.filter((d: Row) => d.selectable)}
      onSave={(v, key, form) => {
        const member = setup.memberships?.find((m: Row) => m.id === v.membershipId);
        return command({
          action: 'Review service',
          serviceId: service.id,
          serviceVersion: service.version,
          status: v.status,
          details: {
            requirements: v.requirements,
            authority: v.authority,
            reviewNote: v.serviceReview,
            conditions,
            effectiveOn: v.effectiveOn || null,
            recipientPersonId: v.recipientPersonId || null,
            recipientOrganisationId: v.recipientOrganisationId || null,
            method: v.method ?? '',
            registrationReference: v.registrationReference ?? '',
            executionReviewed: form.has('executionReviewed'),
            membershipId: member?.id ?? null,
            companyId: member?.organisation_id ?? null,
            outgoingMembershipVersion: member?.version ?? null
          },
          documentIds: form.getAll('documentIds'),
          reason: v.reason,
          idempotencyKey: key
        });
      }}
    >
      <Select
        name="status"
        label="Service review outcome"
        options={['In progress', 'Ready', ...(service.status === 'Ready' ? ['Completed'] : []), 'Not required', 'Cancelled'].map((s) => [s, s])}
      />
      <Text name="requirements" label="Requirements for this service" type="textarea" value={d.requirements} />
      <Text name="authority" label="Authority, lease or title requirement" type="textarea" value={d.authority} />
      <Text name="serviceReview" label="Service review findings" type="textarea" value={d.reviewNote} />
      <fieldset>
        <legend>Completion conditions</legend>
        {conditions.map((c, n) => (
          <label key={n}>
            <input type="checkbox" checked={c.satisfied} onChange={(e) => setConditions((old) => old.map((x, i) => (i === n ? { ...x, satisfied: e.target.checked } : x)))} />
            {c.label}
          </label>
        ))}
        <label>
          Additional condition
          <input value={newCondition} onChange={(e) => setNewCondition(e.target.value)} />
        </label>
        <button
          type="button"
          disabled={newCondition.trim().length < 3}
          onClick={() => {
            setConditions((old) => [...old, { label: newCondition.trim(), satisfied: false }]);
            setNewCondition('');
          }}
        >
          Add condition
        </button>
      </fieldset>
      <Text name="effectiveOn" label={notice ? 'Actual notice receipt or service date' : 'Actual effective or execution date'} dateOnly value={d.effectiveOn} optional />
      {notice && (
        <>
          <Select name="recipientPersonId" label="Notice recipient contact" value={d.recipientPersonId} optional options={setup.people.map((p: Row) => [p.id, p.display_name])} />
          <Select
            name="recipientOrganisationId"
            label="Notice recipient organisation"
            value={d.recipientOrganisationId}
            optional
            options={setup.organisations.map((o: Row) => [o.id, o.legal_name])}
          />
          <Text name="method" label="Actual service or receipt method" value={d.method} />
          <Text name="registrationReference" label="Registration reference" value={d.registrationReference} />
        </>
      )}
      <label>
        <input type="checkbox" name="executionReviewed" defaultChecked={d.executionReviewed ?? false} />
        Authorised execution or consent evidence has been reviewed, where required
      </label>
      {membership && (
        <Select
          name="membershipId"
          label="Outgoing company membership"
          value={d.membershipId}
          options={(setup.memberships ?? []).map((m: Row) => [m.id, `${m.company_name} — ${m.person_name} — ${m.membership_role}`])}
        />
      )}
      <p>Use Ready after review, then Completed against the same conditions and evidence. This does not execute a legal document or send a notice.</p>
    </Action>
  );
}
export function ConveyancingTemplateForm({ setup, jurisdiction, busy, save }: { setup: Row; jurisdiction: string; busy: boolean; save: Save }) {
  const [questions, setQuestions] = useState<Row[]>(setup.defaultPackQuestions.map((q: Row) => ({ ...q }))),
    [question, setQuestion] = useState('');
  return (
    <Action
      title="Configure a new pack question template"
      busy={busy}
      documents={setup.documents.filter((d: Row) => d.selectable)}
      onSave={(v, key, form) =>
        save(
          {
            building: setup.building,
            title: v.title,
            jurisdiction,
            questions,
            authority: v.authority,
            documentIds: form.getAll('documentIds'),
            reason: v.reason,
            idempotencyKey: key
          },
          '/api/conveyancing/templates'
        )
      }
    >
      <Text name="title" label="Template title and revision" />
      <p>Jurisdiction: {jurisdiction}. Retain the approved question set and form provenance; an issued version is never edited.</p>
      <Text name="authority" label="Template source, use rights and review authority" type="textarea" />
      {questions.map((q, n) => (
        <fieldset key={q.key}>
          <legend>Question {n + 1}</legend>
          <label>
            Section
            <input
              aria-label={'Section for question ' + (n + 1)}
              value={q.section}
              onChange={(e) => setQuestions((old) => old.map((x, i) => (i === n ? { ...x, section: e.target.value } : x)))}
              required
            />
          </label>
          <label>
            Question
            <textarea
              aria-label={'Question ' + (n + 1)}
              value={q.question}
              onChange={(e) => setQuestions((old) => old.map((x, i) => (i === n ? { ...x, question: e.target.value } : x)))}
              required
            />
          </label>
          <label>
            <input type="checkbox" checked={q.required} onChange={(e) => setQuestions((old) => old.map((x, i) => (i === n ? { ...x, required: e.target.checked } : x)))} />
            Required in this template
          </label>
          <button type="button" onClick={() => setQuestions((old) => old.filter((_, i) => i !== n))}>
            Remove question {n + 1}
          </button>
        </fieldset>
      ))}
      <label>
        New question
        <textarea value={question} onChange={(e) => setQuestion(e.target.value)} />
      </label>
      <button
        type="button"
        disabled={question.trim().length < 3}
        onClick={() => {
          setQuestions((old) => [...old, { key: 'question-' + crypto.randomUUID(), section: 'Additional enquiries', question: question.trim(), required: true }]);
          setQuestion('');
        }}
      >
        Add question
      </button>
    </Action>
  );
}
